<template>
  <div>
    <h1 class="my-5 text-4xl font-extrabold tracking-tight text-gray-900">Sélectionnez le produit concerné</h1>
    <Selector
      id="productId"
      label="Pour quel produit souhaitez-vous déclarer un sinistre ?"
      name="productId"
      v-model="productId"
      :options="productOptions"
      required
    />

    <div v-if="alternativeProductSelected" class="mt-5">
      <h2 class="my-5 text-2xl font-bold tracking-tight text-gray-900">
        Veuillez entrer les informations de l'appareil sinistré
      </h2>
      <ManualClaimProductFields v-model="manualProductValues" />
    </div>

    <div class="mt-5 flex justify-between gap-5">
      <GenericButton @click="previousStep" secondary no-scaling class="mr-2">Précédent</GenericButton>
      <div class="pl-2">
        <NextButton @click="nextStep" :disabled="!requiredFieldsFilled" :saving="saving" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  ClaimDeclarationFormStep as Step,
  ClaimDeclarationFormNextButton as NextButton,
  GenericButton,
  Selector,
} from "@estaly/ui";
import ManualClaimProductFields, {
  MANUAL_PRODUCT_FIELDS,
  MANUAL_PRODUCT_FORM_DATA_KEYS,
  emptyManualProductValues,
} from "@/components/claim/form/ManualClaimProductFields.vue";
import { mapGetters } from "vuex";

const ALTERNATIVE_PRODUCT_VALUE = "__alternative__";

export default {
  extends: Step,
  components: {
    GenericButton,
    NextButton,
    Selector,
    ManualClaimProductFields,
  },
  data() {
    return {
      productId: null,
      manualProductValues: emptyManualProductValues(),
      requiredFieldsFilled: false,
    };
  },
  computed: {
    ...mapGetters("claimDeclarations", ["planSaleWithRequiredProduct", "canDeclareAlternativeProduct"]),

    productOptions() {
      const options = (this.products || []).map((product) => ({
        label: product.title,
        value: product.id,
      }));
      if (this.canDeclareAlternativeProduct) {
        options.push({
          label: "Un autre appareil",
          value: ALTERNATIVE_PRODUCT_VALUE,
        });
      }
      return options;
    },

    alternativeProductSelected() {
      return this.productId === ALTERNATIVE_PRODUCT_VALUE;
    },

    skipStep() {
      // The step is meaningful only when the user actually has a choice:
      // - several insured products to pick from, OR
      // - the ability to declare an alternative device (MPCP).
      if (this.canDeclareAlternativeProduct) return false;
      if (this.planSaleWithRequiredProduct && (this.products?.length ?? 0) > 1) return false;
      return true;
    },
  },
  mounted() {
    const queryProductId = this.$route?.query?.product_id;
    if (queryProductId) {
      this.productId = queryProductId;
    } else if (this.canDeclareAlternativeProduct && (!this.products || this.products.length === 0)) {
      this.productId = ALTERNATIVE_PRODUCT_VALUE;
    }
  },
  watch: {
    productId(value) {
      this.applyProductSelection(value);
      this.updateRequiredFieldsFilled();
    },
    manualProductValues: {
      deep: true,
      handler(values) {
        if (!this.alternativeProductSelected) return;
        MANUAL_PRODUCT_FIELDS.forEach((field) => {
          this.setFormData(MANUAL_PRODUCT_FORM_DATA_KEYS[field], values[field]);
        });
        this.updateRequiredFieldsFilled();
      },
    },
    currentStep(step, previous) {
      if (step !== this.componentStep) return;
      if (!this.skipStep) return;
      // Clean & skip
      this.save();
      previous < step ? this.$emit("next") : this.$emit("previous");
    },
  },
  methods: {
    applyProductSelection(value) {
      if (value === ALTERNATIVE_PRODUCT_VALUE) {
        this.setFormData("product_id", null);
        // Re-emit current manual values so they are written into the FormData.
        MANUAL_PRODUCT_FIELDS.forEach((field) => {
          this.setFormData(MANUAL_PRODUCT_FORM_DATA_KEYS[field], this.manualProductValues[field]);
        });
        return;
      }

      // Insured product selected (or cleared) — drop any manual fields.
      MANUAL_PRODUCT_FIELDS.forEach((field) => {
        this.setFormData(MANUAL_PRODUCT_FORM_DATA_KEYS[field], null);
      });
      if (value !== undefined && value !== null) {
        this.formData.set("product_id", value);
      } else {
        this.formData.delete("product_id");
      }
    },

    manualFieldsFilled() {
      return MANUAL_PRODUCT_FIELDS.every((field) => this.formData.get(MANUAL_PRODUCT_FORM_DATA_KEYS[field]));
    },

    updateRequiredFieldsFilled() {
      if (this.alternativeProductSelected) {
        this.requiredFieldsFilled = this.manualFieldsFilled();
        return;
      }
      this.requiredFieldsFilled = !!this.formData.get("product_id");
    },
  },
};
</script>
